/**
 * Utilidad para gestionar, formatear y calcular el estado de apertura (Abierto / Cerrado)
 * de una tienda en base al horario configurado y la hora local actual.
 */

export const HORARIO_DEFAULT = {
    lv: { activo: true, apertura: '08:00', cierre: '20:00' },
    sab: { activo: true, apertura: '09:00', cierre: '21:00' },
    dom: { activo: false, apertura: '10:00', cierre: '16:00' },
};

/**
 * Convierte un objeto de horario en un texto legible para mostrar
 */
export function generarTextoHorario(horarioObj) {
    if (!horarioObj) return 'Horario no especificado';

    const partes = [];

    if (horarioObj.lv?.activo) {
        partes.push(`Lun-Vie: ${horarioObj.lv.apertura} a ${horarioObj.lv.cierre}`);
    } else {
        partes.push('Lun-Vie: Cerrado');
    }

    if (horarioObj.sab?.activo) {
        partes.push(`Sáb: ${horarioObj.sab.apertura} a ${horarioObj.sab.cierre}`);
    } else {
        partes.push('Sáb: Cerrado');
    }

    if (horarioObj.dom?.activo) {
        partes.push(`Dom: ${horarioObj.dom.apertura} a ${horarioObj.dom.cierre}`);
    } else {
        partes.push('Dom: Cerrado');
    }

    return partes.join(' | ');
}

/**
 * Parsea el valor almacenado en BD (puede ser JSON string o texto libre)
 */
export function parsearHorario(horarioRaw) {
    if (!horarioRaw) return { ...HORARIO_DEFAULT, textoLibre: '' };

    if (typeof horarioRaw === 'object') {
        return {
            lv: horarioRaw.lv || HORARIO_DEFAULT.lv,
            sab: horarioRaw.sab || HORARIO_DEFAULT.sab,
            dom: horarioRaw.dom || HORARIO_DEFAULT.dom,
            textoLibre: horarioRaw.texto || ''
        };
    }

    try {
        const parsed = JSON.parse(horarioRaw);
        if (parsed && (parsed.lv || parsed.sab || parsed.dom)) {
            return {
                lv: parsed.lv || HORARIO_DEFAULT.lv,
                sab: parsed.sab || HORARIO_DEFAULT.sab,
                dom: parsed.dom || HORARIO_DEFAULT.dom,
                textoLibre: parsed.texto || ''
            };
        }
    } catch (e) {
        // No era JSON, es texto plano
    }

    // Si es texto plano antiguo, tratamos de extraer horas simples como "08:00 - 20:00"
    return {
        ...HORARIO_DEFAULT,
        textoLibre: String(horarioRaw)
    };
}

/**
 * Evalúa si la tienda está ABIERTA o CERRADA en este momento exacto
 * @param {string|object} horarioRaw - Campo horario de la tienda
 * @returns {{ abierto: boolean, estado: string, mensaje: string, detalle: string, resumen: string }}
 */
export function calcularEstadoHorario(horarioRaw) {
    if (!horarioRaw) {
        return {
            abierto: false,
            estado: 'cerrado',
            mensaje: 'Sin horario',
            detalle: 'Horario no especificado',
            resumen: 'Horario no especificado'
        };
    }

    const config = parsearHorario(horarioRaw);
    const ahora = new Date();
    const diaSemana = ahora.getDay(); // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado
    const minutosActuales = ahora.getHours() * 60 + ahora.getMinutes();

    let configDia = null;
    let nombreDia = '';

    if (diaSemana >= 1 && diaSemana <= 5) {
        configDia = config.lv;
        nombreDia = 'Lunes a Viernes';
    } else if (diaSemana === 6) {
        configDia = config.sab;
        nombreDia = 'Sábado';
    } else if (diaSemana === 0) {
        configDia = config.dom;
        nombreDia = 'Domingo';
    }

    const resumenTexto = config.textoLibre || generarTextoHorario(config);

    // Si el día no atiende
    if (!configDia || !configDia.activo) {
        return {
            abierto: false,
            estado: 'cerrado',
            mensaje: 'Cerrado hoy',
            detalle: `Cerrado los ${nombreDia}`,
            resumen: resumenTexto
        };
    }

    // Convertir 'HH:MM' a minutos desde la medianoche
    const aMinutos = (timeStr) => {
        if (!timeStr) return 0;
        const [h, m] = timeStr.split(':').map(Number);
        return (h || 0) * 60 + (m || 0);
    };

    const minApertura = aMinutos(configDia.apertura);
    const minCierre = aMinutos(configDia.cierre);

    // Caso de horario normal (ej: 08:00 a 20:00)
    if (minApertura < minCierre) {
        if (minutosActuales >= minApertura && minutosActuales < minCierre) {
            return {
                abierto: true,
                estado: 'abierto',
                mensaje: 'Abierto',
                detalle: `Cierra a las ${configDia.cierre}`,
                resumen: resumenTexto
            };
        } else {
            return {
                abierto: false,
                estado: 'cerrado',
                mensaje: 'Cerrado',
                detalle: minutosActuales < minApertura ? `Abre a las ${configDia.apertura}` : `Cerró a las ${configDia.cierre}`,
                resumen: resumenTexto
            };
        }
    } 
    // Caso de horario nocturno que pasa de medianoche (ej: 18:00 a 03:00)
    else if (minApertura > minCierre) {
        if (minutosActuales >= minApertura || minutosActuales < minCierre) {
            return {
                abierto: true,
                estado: 'abierto',
                mensaje: 'Abierto',
                detalle: `Cierra a las ${configDia.cierre}`,
                resumen: resumenTexto
            };
        } else {
            return {
                abierto: false,
                estado: 'cerrado',
                mensaje: 'Cerrado',
                detalle: `Abre a las ${configDia.apertura}`,
                resumen: resumenTexto
            };
        }
    }

    return {
        abierto: true,
        estado: 'abierto',
        mensaje: 'Abierto',
        detalle: 'Atención continua',
        resumen: resumenTexto
    };
}
